import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Images, X, Loader2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import Lightbox from "@/components/Lightbox";

export default function PhotoFlyout() {
  const { media, mediaReady, flyoutOpen, setFlyoutOpen } = useSite();
  const [lightbox, setLightbox] = useState(null);

  const items = media.map((m) => ({
    url: m.url,
    title: m.caption || "Listing photo",
    desc: "52-461 Beechwood Place, Waterloo",
  }));

  return (
    <>
      <motion.button
        data-testid="flyout-gallery-button"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        onClick={() => setFlyoutOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-3.5 text-sm font-semibold text-white shadow-2xl transition-colors duration-200 hover:bg-bronze dark:bg-white dark:text-black dark:hover:bg-bronze dark:hover:text-white"
      >
        <Images size={16} /> Photos
        <span className="rounded-full bg-bronze px-2 py-0.5 text-xs font-bold text-white">
          {media.length}
        </span>
      </motion.button>

      <AnimatePresence>
        {flyoutOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFlyoutOpen(false)}
              className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm"
            />
            <motion.aside
              data-testid="flyout-gallery-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="fixed right-0 top-0 z-[95] flex h-full w-80 max-w-[85vw] flex-col border-l border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900"
            >
              <div className="flex items-center justify-between border-b border-zinc-200 p-5 dark:border-zinc-800">
                <div>
                  <p className="font-display text-sm font-extrabold tracking-[0.15em] text-zinc-900 dark:text-white">
                    461 BEECHWOOD
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {items.length} listing {items.length === 1 ? "photo" : "photos"}
                  </p>
                </div>
                <button
                  data-testid="flyout-gallery-close-button"
                  aria-label="Close photo gallery"
                  onClick={() => setFlyoutOpen(false)}
                  className="rounded-full border border-zinc-200 p-2 text-zinc-600 transition-colors duration-200 hover:border-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-white"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="grid flex-1 grid-cols-2 content-start gap-2 overflow-y-auto p-4">
                {!mediaReady && (
                  <div className="col-span-2 flex justify-center py-12">
                    <Loader2 size={26} className="animate-spin text-bronze" />
                  </div>
                )}
                {mediaReady && items.length === 0 && (
                  <div className="col-span-2 rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                    No photos uploaded yet.
                  </div>
                )}
                {items.map((it, i) => (
                  <button
                    key={it.url}
                    data-testid={`flyout-photo-thumb-${i}`}
                    onClick={() => setLightbox(i)}
                    className="group relative aspect-square overflow-hidden rounded-xl"
                  >
                    <img
                      src={it.url}
                      alt={it.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {media[i]?.role === "hero" && (
                      <span className="absolute left-1.5 top-1.5 rounded-full bg-bronze px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                        Hero
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox
            items={items}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNav={(dir) =>
              setLightbox((i) => (i === null ? null : (i + dir + items.length) % items.length))
            }
          />
        )}
      </AnimatePresence>
    </>
  );
}
